import { Form, Input, Modal, notification, Space, Tabs, Typography } from 'antd'
import { useState } from 'react'
import MarkdownContent from '../../../AiAssistant/MarkdownContent'
import {
  buildRubricUpdateInput,
  Rubric,
  validateRubric,
} from './rubricsAdminModel'
import {
  useCreateRubricMutation,
  useUpdateRubricMutation,
} from './rubricsAdminQueries'

type Props = {
  open: boolean
  onClose: () => void
  /** When provided the modal edits this rubric; otherwise it creates a new one. */
  rubric?: Rubric
}

const RubricEditorModal = ({ open, onClose, rubric }: Props) => {
  const isEdit = rubric != null
  // In edit mode the existing rubric seeds the form. The name is immutable on
  // the backend, so only the markdown body is editable.
  const [name, setName] = useState(rubric?.name ?? '')
  const [body, setBody] = useState(rubric?.body ?? '')
  const createMutation = useCreateRubricMutation()
  const updateMutation = useUpdateRubricMutation()
  const mutation = isEdit ? updateMutation : createMutation

  const reset = () => {
    setName(rubric?.name ?? '')
    setBody(rubric?.body ?? '')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleOk = () => {
    const expectedName = isEdit && rubric ? rubric.name : name
    const validationError = validateRubric(expectedName, body)
    if (validationError) {
      notification.error({ message: 'Invalid rubric', description: validationError })
      return
    }
    if (isEdit && rubric) {
      updateMutation.mutate(buildRubricUpdateInput(rubric, body), {
        onSuccess: handleClose,
      })
      return
    }
    createMutation.mutate({ name: name.trim(), body }, { onSuccess: handleClose })
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      onOk={handleOk}
      okText="Save"
      title={isEdit ? 'Edit Rubric' : 'New Rubric'}
      width={820}
      confirmLoading={mutation.isLoading}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Form layout="horizontal" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
          <Form.Item
            label="Name"
            style={{ marginBottom: 0 }}
            // The name is the key cases reference by; the backend cannot rename a
            // rubric on update, so it is read-only when editing.
            extra={isEdit ? 'The name is immutable; editing saves a new version of the body.' : undefined}
          >
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. task-completion"
              disabled={isEdit}
            />
          </Form.Item>
        </Form>
        <Tabs
          items={[
            {
              key: 'write',
              label: 'Write',
              children: (
                <Input.TextArea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="# Rubric&#10;&#10;Describe the scoring criteria in markdown…"
                  autoSize={{ minRows: 16, maxRows: 28 }}
                  style={{ fontFamily: 'monospace' }}
                />
              ),
            },
            {
              key: 'preview',
              label: 'Preview',
              children: body.trim() ? (
                <div
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: '8px 16px',
                    minHeight: 320,
                    maxHeight: 560,
                    overflowY: 'auto',
                  }}
                >
                  <MarkdownContent content={body} />
                </div>
              ) : (
                <Typography.Text type="secondary">Nothing to preview yet.</Typography.Text>
              ),
            },
          ]}
        />
      </Space>
    </Modal>
  )
}

export default RubricEditorModal
